import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import TimeZoneSelector from '@/core/TimeZoneSelector.molecule';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps, getFormFieldWrapperProps } from '@/formbuilder/formbuilder.utilities';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { Timezone } from '@/datetime/timezone.service';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { ImportDatafileTimezone } from '@/tools/importDatafile/importDatafile.store';
import type { BivariantCallback } from '@/utilities.types';
import type { TooltipPosition } from '@seeqdev/qomponents';

export interface TimeZoneSelectorIF extends ValidatingFormComponent<
  Timezone | ImportDatafileTimezone | { name: string } | undefined
> {
  component: 'TimeZoneSelectorFormComponent';
  onChange?: BivariantCallback<Timezone | ImportDatafileTimezone | { name: string } | undefined>;
  value?: Timezone | ImportDatafileTimezone | { name: string };
  optional?: boolean;
  disabled?: boolean;
  timezoneSet?: boolean;
  defaultTimeZone?: Timezone;
  direction?: 'top' | 'bottom' | 'auto';
  enableAutoDetect?: boolean;
  tooltipPlacement?: TooltipPosition;
}

export const TimeZoneSelectorFormComponent = (props: TimeZoneSelectorIF) => {
  const {
    name,
    validation,
    defaultTimeZone,
    skipToFormState,
    value,
    testId,
    enableAutoDetect = true,
    tooltipPlacement = 'left',
  } = props;

  const { t } = useTranslation();
  const defaultValidation = (timezoneObj: Timezone | ImportDatafileTimezone | { name: string } | undefined) =>
    _.isEmpty((timezoneObj as Timezone | { name: string } | undefined)?.name);
  const formState = useForm().getState();
  const timezoneSet = !_.isEmpty(formState.values[name]);

  return (
    <FormFieldWrapper
      {...getFormFieldWrapperProps(props, ['wrapperClassNames'])}
      wrapperXstyle={[styles.field, props.wrapperXstyle]}>
      <Field<Timezone | ImportDatafileTimezone | { name: string } | undefined>
        name={name}
        validate={validation || defaultValidation}>
        {({ input, meta }) => {
          const formFieldProps = getFormFieldProps(formState, input, meta, props);
          const { onChange } = formFieldProps;

          return (
            <>
              <TimeZoneSelector
                {...formFieldProps}
                onSelect={onChange}
                timezone={skipToFormState ? input.value : value}
                testId={testId ? `${testId}Field` : ''}
              />

              {enableAutoDetect && defaultTimeZone && (
                <div {...stylex.props(styles.resetRow)}>
                  <HoverTooltip placement={tooltipPlacement} text="USER.RESET_TOOLTIP">
                    <span
                      {...stylex.props(styles.resetLink, timezoneSet ? styles.resetDefault : styles.resetRequired)}
                      onClick={() => onChange(defaultTimeZone)}>
                      {t('USER.RESET')}
                    </span>
                  </HoverTooltip>
                </div>
              )}
            </>
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};

const styles = stylex.create({
  field: { flex: '1' },
  resetRow: { display: 'flex', flexDirection: 'row', marginTop: '.3125rem', marginLeft: '.3125rem' },
  resetLink: { cursor: 'pointer', fontStyle: 'italic', textDecoration: 'underline' },
  resetDefault: { color: colors.legacyToolsLink, fontSize: 'smaller' },
  resetRequired: { color: colors.borderInvalid },
});
